import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Heading,
  Spinner,
  Text,
  TextField,
  TagBadge,
  Banner,
} from '@chatsdet/ui';
import { Copy, Check, ShieldCheck, ShieldWarning } from '@phosphor-icons/react';
import { ApiError } from '../../../lib/api-error';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useAccountMfaEnrollStart,
  useAccountMfaEnrollConfirm,
  useAccountMfaRegenerateRecoveryCodes,
  useAccountMfaRemove,
} from '../../../hooks/useMfa';
import { copyTextToClipboard } from './clipboard-copy';

function apiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * Own-account MFA (TOTP) management. Status is read straight off `useAuth()`
 * (`user.mfaEnrolled` / `user.mfaRequired`) so it stays in sync with the org
 * toggle in the Organization section without a second fetch.
 */
export function SecuritySection() {
  const { user } = useAuth();
  // Lifted above the mfaEnrolled branch below: confirming enrollment
  // invalidates `['me']`, which flips `user.mfaEnrolled` to true as soon as
  // the refetch resolves — often before the user has acknowledged the
  // one-time recovery codes. Keeping this state here (rather than inside
  // whichever panel is enrolling) means the codes stay on screen across that
  // flip instead of being unmounted out from under the user.
  const [pendingRecoveryCodes, setPendingRecoveryCodes] = React.useState<string[] | null>(null);

  return (
    <Box className="space-y-6">
      <div className="space-y-1">
        <Heading level={2}>Security</Heading>
        <Text size="sm" className="text-muted-foreground">
          Manage two-factor authentication for your account.
        </Text>
      </div>

      {pendingRecoveryCodes ? (
        <RecoveryCodesDisplay
          codes={pendingRecoveryCodes}
          onDone={() => setPendingRecoveryCodes(null)}
        />
      ) : user?.mfaEnrolled ? (
        <EnrolledPanel mfaRequired={!!user.mfaRequired} />
      ) : (
        <NotEnrolledPanel
          mfaRequired={!!user?.mfaRequired}
          onEnrolled={setPendingRecoveryCodes}
        />
      )}
    </Box>
  );
}

function NotEnrolledPanel({
  mfaRequired,
  onEnrolled,
}: {
  mfaRequired: boolean;
  onEnrolled: (codes: string[]) => void;
}) {
  const [isSettingUp, setIsSettingUp] = React.useState(false);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4">
        <Flex align="start" gap="md">
          <div className="p-2 bg-muted rounded-lg">
            <ShieldWarning size={24} className="text-muted-foreground" />
          </div>
          <div className="flex-1 space-y-1">
            <Flex align="center" gap="sm">
              <Text weight="medium">Authenticator app</Text>
              <TagBadge name="Not enrolled" color="gray" hideDot size="sm" />
            </Flex>
            <Text size="sm" className="text-muted-foreground">
              Add a time-based one-time code from an app like Google Authenticator, 1Password, or
              Authy as a second factor when you sign in.
            </Text>
            {mfaRequired && (
              <Text size="sm" className="text-warning">
                Your organization requires this — you&apos;ll be asked to set it up on your next
                sign-in if you don&apos;t do it now.
              </Text>
            )}
          </div>
        </Flex>
      </div>

      <Flex justify="end">
        <Button variant="primary" onPress={() => setIsSettingUp(true)}>
          Set up authenticator app
        </Button>
      </Flex>

      {isSettingUp && (
        <EnrollFlow
          onClose={() => setIsSettingUp(false)}
          onEnrolled={(codes) => {
            setIsSettingUp(false);
            onEnrolled(codes);
          }}
        />
      )}
    </div>
  );
}

function EnrollFlow({
  onClose,
  onEnrolled,
}: {
  onClose: () => void;
  onEnrolled: (codes: string[]) => void;
}) {
  const startMutation = useAccountMfaEnrollStart();
  const confirmMutation = useAccountMfaEnrollConfirm();

  const [code, setCode] = React.useState('');
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  const startMutate = startMutation.mutate;
  React.useEffect(() => {
    startMutate();
    // Fire once when the flow mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setConfirmError(null);
    confirmMutation.mutate(code, {
      onSuccess: (data) => onEnrolled(data.recoveryCodes),
      onError: (err) =>
        setConfirmError(apiErrorMessage(err, 'That code did not work. Please try again.')),
    });
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-6">
      {startMutation.isPending && (
        <Flex align="center" justify="center" className="py-8">
          <Spinner size="lg" />
        </Flex>
      )}

      {startMutation.isError && (
        <div className="space-y-3 text-center">
          <Text size="sm" className="text-destructive">
            {apiErrorMessage(startMutation.error, 'Failed to start MFA setup.')}
          </Text>
          <Button variant="outline" onPress={() => startMutation.mutate()}>
            Try again
          </Button>
        </div>
      )}

      {startMutation.data && (
        <>
          <div className="space-y-3 text-center">
            <Text size="sm" className="text-muted-foreground">
              Scan this QR code with your authenticator app.
            </Text>
            <div className="flex justify-center">
              <img
                src={startMutation.data.qrSvgDataUri}
                alt="Authenticator app QR code"
                className="h-40 w-40 rounded-md border border-border bg-white p-2"
              />
            </div>
            <div className="space-y-1">
              <Text size="xs" className="text-muted-foreground">
                Can&apos;t scan? Enter this key manually:
              </Text>
              <code
                className="block select-all break-all rounded-md bg-muted px-3 py-2 text-xs font-mono"
                aria-label="Authenticator setup key"
              >
                {startMutation.data.secret}
              </code>
            </div>
          </div>

          <form onSubmit={handleConfirm} className="space-y-4">
            {confirmError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <Text size="sm" className="text-destructive">
                  {confirmError}
                </Text>
              </div>
            )}
            <TextField
              label="Enter the 6-digit code from your app"
              value={code}
              onChange={(val) => setCode(val.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="max-w-xs"
              autoFocus
            />
            <Flex gap="sm">
              <Button
                type="submit"
                variant="primary"
                isDisabled={confirmMutation.isPending || code.length !== 6}
              >
                {confirmMutation.isPending ? 'Confirming...' : 'Confirm'}
              </Button>
              <Button variant="outline" onPress={onClose} isDisabled={confirmMutation.isPending}>
                Cancel
              </Button>
            </Flex>
          </form>
        </>
      )}
    </div>
  );
}

function RecoveryCodesDisplay({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-4">
      <Banner variant="warning">
        Save these recovery codes now. They are shown only once and cannot be retrieved again.
      </Banner>

      <div className="rounded-md border border-border bg-muted p-4">
        <div className="grid grid-cols-2 gap-2 font-mono text-sm">
          {codes.map((c) => (
            <div key={c} className="select-all">
              {c}
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="outline"
        iconLeft={copied ? <Check size={16} /> : <Copy size={16} />}
        onPress={async () => {
          const ok = await copyTextToClipboard(codes.join('\n'));
          if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy all codes'}
      </Button>

      <Checkbox
        isSelected={acknowledged}
        onChange={setAcknowledged}
        label="I've saved these recovery codes in a safe place"
      />

      <Flex justify="end">
        <Button variant="primary" isDisabled={!acknowledged} onPress={onDone}>
          Done
        </Button>
      </Flex>
    </div>
  );
}

function EnrolledPanel({ mfaRequired }: { mfaRequired: boolean }) {
  const [isRegenOpen, setIsRegenOpen] = React.useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [regeneratedCodes, setRegeneratedCodes] = React.useState<string[] | null>(null);

  const regenerateMutation = useAccountMfaRegenerateRecoveryCodes();
  const removeMutation = useAccountMfaRemove();

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4">
        <Flex align="start" gap="md">
          <div className="p-2 bg-success/10 rounded-lg">
            <ShieldCheck size={24} className="text-success" />
          </div>
          <div className="flex-1 space-y-1">
            <Flex align="center" gap="sm">
              <Text weight="medium">Authenticator app</Text>
              <TagBadge name="Enrolled" color="green" hideDot size="sm" />
            </Flex>
            <Text size="sm" className="text-muted-foreground">
              Two-factor authentication is active on your account.
            </Text>
          </div>
        </Flex>
      </div>

      <Flex gap="sm">
        <Button variant="outline" onPress={() => setIsRegenOpen(true)}>
          Regenerate recovery codes
        </Button>
        {mfaRequired ? (
          <Text size="sm" className="text-muted-foreground self-center">
            Your organization requires MFA — an admin must reset it before you can remove it.
          </Text>
        ) : (
          <Button variant="destructive" onPress={() => setIsRemoveOpen(true)}>
            Remove authenticator
          </Button>
        )}
      </Flex>

      {/* Regenerate recovery codes dialog */}
      <DialogTrigger
        isOpen={isRegenOpen}
        onOpenChange={(open) => {
          setIsRegenOpen(open);
          if (!open) setRegeneratedCodes(null);
        }}
      >
        <span />
        <Dialog title="Regenerate recovery codes">
          <DialogContent>
            {regeneratedCodes ? (
              <RecoveryCodesDisplay
                codes={regeneratedCodes}
                onDone={() => setIsRegenOpen(false)}
              />
            ) : (
              <div className="space-y-3">
                <Text size="sm" className="text-muted-foreground">
                  This invalidates your existing recovery codes and generates a new set. Anyone
                  holding the old codes will no longer be able to use them.
                </Text>
                {regenerateMutation.isError && (
                  <Text size="sm" className="text-destructive">
                    {apiErrorMessage(regenerateMutation.error, 'Failed to regenerate codes.')}
                  </Text>
                )}
              </div>
            )}
          </DialogContent>
          {!regeneratedCodes && (
            <DialogFooter>
              <Button
                variant="outline"
                onPress={() => setIsRegenOpen(false)}
                isDisabled={regenerateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                isDisabled={regenerateMutation.isPending}
                onPress={() =>
                  regenerateMutation.mutate(undefined, {
                    onSuccess: (data) => setRegeneratedCodes(data.recoveryCodes),
                  })
                }
              >
                {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate'}
              </Button>
            </DialogFooter>
          )}
        </Dialog>
      </DialogTrigger>

      {/* Remove authenticator dialog */}
      <DialogTrigger isOpen={isRemoveOpen} onOpenChange={setIsRemoveOpen}>
        <span />
        <Dialog title="Remove authenticator app">
          <DialogContent>
            <Text size="sm" className="text-muted-foreground">
              This removes two-factor authentication from your account. You will no longer be
              asked for a code when signing in.
            </Text>
            {removeMutation.isError && (
              <Text size="sm" className="text-destructive mt-2">
                {apiErrorMessage(removeMutation.error, 'Failed to remove authenticator.')}
              </Text>
            )}
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsRemoveOpen(false)}
              isDisabled={removeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              isDisabled={removeMutation.isPending}
              onPress={() =>
                removeMutation.mutate(undefined, {
                  onSuccess: () => setIsRemoveOpen(false),
                })
              }
            >
              {removeMutation.isPending ? 'Removing...' : 'Remove authenticator'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </div>
  );
}
